/**
 * CompanyFormModal — create / edit a company (Phase 10.5).
 *
 * Sections: Identifikace · Adresa · Bankovní spojení · Moduly · LMS pobočky · Místa předání.
 * Module access is a `moduly: string[]` array (access overhaul Stage 6) —
 * checked = module present in the array. Edit mode appends a "Členové"
 * block listing the company's users with an inline primárni toggle,
 * remove buttons, and a "+ Přidat uživatele" picker via
 * `MembershipPickerModal`; per-member module grants live in the Členové tab.
 *
 * Delete surfaces 409 blocking-deps in `CompanyDeleteModal`, which lets a
 * platform admin force-delete the company including all its data after
 * typing the exact company name.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Field, Fields } from '@/components/Field';
import { useConfirm } from '@/components/ConfirmDialog';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/IconButton';
import { Marker } from '@/components/Marker';
import { Modal, DialogCrown } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import {
  useAddCompanyMemberMutation,
  useCompanyMembersQuery,
  useCreateCompanyMutation,
  useDeleteCompanyMutation,
  useRemoveCompanyMemberMutation,
  useSkupinyQuery,
  useUpdateCompanyMemberMutation,
  useUpdateCompanyMutation,
  useCompanySlackQuery,
  useUpdateCompanySlackMutation,
  useTestCompanySlackMutation,
  type CompanyCreateBody,
  type CompanyDeleteBlocked,
  type CompanyLmsPobockaInput,
  type CompanyMistoPredaniInput,
  type CompanyModul,
  type CompanyUpdateBody,
  type MgmtCompany,
  type MgmtMembership,
} from './api';
import { CompanyDeleteModal } from './CompanyDeleteModal';
import { MembershipPickerModal } from './MembershipPickerModal';

interface CompanyFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  company?: MgmtCompany;
  onOpenChange: (open: boolean) => void;
}

interface CompanyFormState {
  Nazev: string;
  ulice: string;
  cp: string;
  mesto: string;
  psc: string;
  ICO: string;
  DIC: string;
  zapis_OR: string;
  banka: string;
  ucet: string;
  /** Group id as the <select> value; '' = none. */
  id_skupiny: string;
  moduly: CompanyModul[];
  lms_pobocky: LmsPobockaFormRow[];
  mista_predani: MistoPredaniFormRow[];
}

/** One delivery place in the form; `id` = saved place (kept on save). */
interface MistoPredaniFormRow {
  key: string;
  id?: number;
  nazev: string;
  ulice: string;
  psc: string;
  mesto: string;
}

/** One LMS location in the form; `id` = saved location (kept on save). */
interface LmsPobockaFormRow {
  key: string;
  id?: number;
  nazev: string;
  lms_id: string;
  lms_req_key: string;
  lms_res_key: string;
}

let lmsRowSeq = 0;
function newLmsRowKey(): string {
  lmsRowSeq += 1;
  return `new-${lmsRowSeq}`;
}

let mistoRowSeq = 0;
function newMistoRowKey(): string {
  mistoRowSeq += 1;
  return `new-misto-${mistoRowSeq}`;
}

const MODUL_FIELDS: { key: CompanyModul; label: string }[] = [
  { key: 'kontakty', label: 'Kontakty' },
  { key: 'vozidla', label: 'Vozidla' },
  { key: 'ojeta_vozidla', label: 'Ojetá vozidla' },
  { key: 'leasing', label: 'Leasing' },
  { key: 'servis', label: 'Servis' },
  { key: 'uskladneni', label: 'Uskladnění' },
  { key: 'posta', label: 'Pošta' },
  { key: 'kalendar', label: 'Kalendář' },
  { key: 'ukoly', label: 'Úkoly' },
  { key: 'pujcovna', label: 'Půjčovna' },
];

export function CompanyFormModal({ open, mode, company, onOpenChange }: CompanyFormModalProps) {
  const create = useCreateCompanyMutation();
  const update = useUpdateCompanyMutation();
  const remove = useDeleteCompanyMutation();
  const skupiny = useSkupinyQuery(open);

  const [form, setForm] = useState<CompanyFormState>(emptyForm());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [blockedPayload, setBlockedPayload] = useState<CompanyDeleteBlocked | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const { confirm, dialog: confirmDialog } = useConfirm();

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && company) {
      setForm({
        Nazev: company.Nazev,
        ulice: company.ulice,
        cp: company.cp,
        mesto: company.mesto,
        psc: company.psc,
        ICO: String(company.ICO ?? 0),
        DIC: company.DIC,
        zapis_OR: company.zapis_OR,
        banka: company.banka,
        ucet: company.ucet,
        id_skupiny: company.id_skupiny == null ? '' : String(company.id_skupiny),
        moduly: [...company.moduly],
        lms_pobocky: company.lms_pobocky.map((p) => ({
          key: `id-${p.id}`,
          id: p.id,
          nazev: p.nazev,
          lms_id: String(p.lms_id),
          lms_req_key: p.lms_req_key,
          lms_res_key: p.lms_res_key,
        })),
        mista_predani: company.mista_predani.map((m) => ({
          key: `id-${m.id}`,
          id: m.id,
          nazev: m.nazev,
          ulice: m.ulice,
          psc: m.psc,
          mesto: m.mesto,
        })),
      });
    } else {
      setForm(emptyForm());
    }
    setConfirmingDelete(false);
    setBlockedPayload(null);
    queueMicrotask(() => firstFieldRef.current?.focus());
  }, [open, mode, company]);

  const submit = () => {
    const Nazev = form.Nazev.trim();
    if (!Nazev) {
      toast.error('Zadejte název firmy.');
      return;
    }
    const ICO = form.ICO ? Number.parseInt(form.ICO, 10) : 0;
    if (!Number.isFinite(ICO) || ICO < 0) {
      toast.error('Neplatné IČO.');
      return;
    }
    const lms_pobocky: CompanyLmsPobockaInput[] = [];
    for (const row of form.lms_pobocky) {
      // Empty = not filled in yet (0). An incomplete location saves fine; it
      // just can't fetch leads until its DID and request key are set.
      const lms_id = row.lms_id.trim() ? Number.parseInt(row.lms_id, 10) : 0;
      if (!Number.isFinite(lms_id) || lms_id < 0) {
        toast.error('Neplatné LMS ID pobočky.');
        return;
      }
      // Remaining rules (empty rows, names, duplicate DIDs) are enforced by the API.
      lms_pobocky.push({
        ...(row.id !== undefined ? { id: row.id } : {}),
        nazev: row.nazev.trim(),
        lms_id,
        lms_req_key: row.lms_req_key.trim(),
        lms_res_key: row.lms_res_key.trim(),
      });
    }

    const mista_predani: CompanyMistoPredaniInput[] = [];
    for (const row of form.mista_predani) {
      const nazev = row.nazev.trim();
      if (!nazev) {
        toast.error('Každé místo předání musí mít název.');
        return;
      }
      // Duplicate names are enforced by the API.
      mista_predani.push({
        ...(row.id !== undefined ? { id: row.id } : {}),
        nazev,
        ulice: row.ulice.trim(),
        psc: row.psc.trim(),
        mesto: row.mesto.trim(),
      });
    }

    const body: CompanyCreateBody = {
      Nazev,
      ulice: form.ulice.trim(),
      cp: form.cp.trim(),
      mesto: form.mesto.trim(),
      psc: form.psc.trim(),
      ICO,
      DIC: form.DIC.trim(),
      zapis_OR: form.zapis_OR.trim(),
      banka: form.banka.trim(),
      ucet: form.ucet.trim(),
      id_skupiny: form.id_skupiny ? Number(form.id_skupiny) : null,
      moduly: form.moduly,
      lms_pobocky,
      mista_predani,
    };

    if (mode === 'edit' && company) {
      update.mutate(
        { id: company.id_firmy, body: body as CompanyUpdateBody },
        {
          onSuccess: () => {
            toast.ok('Firma aktualizována');
            onOpenChange(false);
          },
          onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se uložit.')),
        },
      );
      return;
    }

    create.mutate(body, {
      onSuccess: () => {
        toast.ok('Firma vytvořena');
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se vytvořit.')),
    });
  };

  const onDelete = () => {
    if (!company) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    remove.mutate({ id: company.id_firmy }, {
      onSuccess: () => {
        toast.ok('Firma smazána');
        onOpenChange(false);
      },
      onError: (err) => {
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          err.body &&
          typeof err.body === 'object' &&
          'blocking' in err.body
        ) {
          const body = err.body as unknown as CompanyDeleteBlocked;
          setBlockedPayload(body);
          setConfirmingDelete(false);
          return;
        }
        toast.error(apiMessage(err, 'Nepodařilo se smazat.'));
      },
    });
  };

  const setLmsRow = (key: string, patch: Partial<LmsPobockaFormRow>) =>
    setForm((f) => ({
      ...f,
      lms_pobocky: f.lms_pobocky.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    }));

  const addLmsRow = () =>
    setForm((f) => ({
      ...f,
      lms_pobocky: [
        ...f.lms_pobocky,
        { key: newLmsRowKey(), nazev: '', lms_id: '', lms_req_key: '', lms_res_key: '' },
      ],
    }));

  const makeMainLmsRow = (key: string) =>
    setForm((f) => {
      const row = f.lms_pobocky.find((r) => r.key === key);
      if (!row) return f;
      return { ...f, lms_pobocky: [row, ...f.lms_pobocky.filter((r) => r.key !== key)] };
    });

  const removeLmsRow = async (row: LmsPobockaFormRow) => {
    // A saved location's leads stop syncing with LMS once it's removed.
    if (
      row.id !== undefined &&
      !(await confirm({
        title: (
          <>
            Odebrat pobočku <em>{row.nazev || `LMS ${row.lms_id}`}</em>?
          </>
        ),
        description:
          'Kontakty převzaté z této pobočky se přestanou synchronizovat s LMS. Projeví se po uložení firmy.',
        tone: 'danger',
        confirmLabel: 'Odebrat',
      }))
    ) {
      return;
    }
    setForm((f) => ({ ...f, lms_pobocky: f.lms_pobocky.filter((r) => r.key !== row.key) }));
  };

  const setMistoRow = (key: string, patch: Partial<MistoPredaniFormRow>) =>
    setForm((f) => ({
      ...f,
      mista_predani: f.mista_predani.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    }));

  const addMistoRow = () =>
    setForm((f) => ({
      ...f,
      mista_predani: [
        ...f.mista_predani,
        { key: newMistoRowKey(), nazev: '', ulice: '', psc: '', mesto: '' },
      ],
    }));

  const removeMistoRow = (key: string) =>
    setForm((f) => ({ ...f, mista_predani: f.mista_predani.filter((r) => r.key !== key) }));

  const isPending = create.isPending || update.isPending;
  const heading = mode === 'edit' ? 'Úprava firmy' : 'Nová firma';

  return (
    <>
      <Modal open={open} onOpenChange={onOpenChange} maxWidth={820}>
        <DialogCrown
          tone="neutral"
          eyebrow={heading}
          eyebrowMeta={mode === 'edit' && company ? `#${company.id_firmy}` : undefined}
          title={
            mode === 'edit' ? (
              <>
                Upravit <em>firmu</em>.
              </>
            ) : (
              <>
                Vytvořit <em>novou firmu</em>.
              </>
            )
          }
        />
        <Modal.Body className="px-8 py-6">
          <SectionHead n="01" title="Identifikace" />
          <Fields cols={2}>
            <Field label="Název" required span>
              <TextInput
                ref={firstFieldRef}
                value={form.Nazev}
                maxLength={100}
                onChange={(e) => setForm({ ...form, Nazev: e.target.value })}
              />
            </Field>
            <Field label="IČO">
              <TextInput
                type="number"
                value={form.ICO}
                onChange={(e) => setForm({ ...form, ICO: e.target.value })}
              />
            </Field>
            <Field label="DIČ">
              <TextInput
                value={form.DIC}
                maxLength={15}
                onChange={(e) => setForm({ ...form, DIC: e.target.value })}
              />
            </Field>
            <Field label="Zápis v OR" span>
              <TextInput
                value={form.zapis_OR}
                maxLength={50}
                onChange={(e) => setForm({ ...form, zapis_OR: e.target.value })}
              />
            </Field>
            <Field
              label="Skupina firem"
              span
              hint="Firmy ve stejné skupině vidí navzájem (jen pro čtení) vozy v servisu a uskladnění a zapisují si termíny STK / servisu."
            >
              <SelectInput
                data-testid="firma-skupina"
                value={form.id_skupiny}
                onChange={(e) => setForm({ ...form, id_skupiny: e.target.value })}
              >
                <option value="">Bez skupiny</option>
                {(skupiny.data?.skupiny ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nazev}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </Fields>

          <div className="mt-6">
            <SectionHead n="02" title="Adresa" />
            <Fields cols={2}>
              <Field label="Ulice">
                <TextInput
                  value={form.ulice}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, ulice: e.target.value })}
                />
              </Field>
              <Field label="Č.p.">
                <TextInput
                  value={form.cp}
                  maxLength={10}
                  onChange={(e) => setForm({ ...form, cp: e.target.value })}
                />
              </Field>
              <Field label="Město">
                <TextInput
                  value={form.mesto}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, mesto: e.target.value })}
                />
              </Field>
              <Field label="PSČ">
                <TextInput
                  value={form.psc}
                  maxLength={6}
                  onChange={(e) => setForm({ ...form, psc: e.target.value })}
                />
              </Field>
            </Fields>
          </div>

          <div className="mt-6">
            <SectionHead n="03" title="Bankovní spojení" />
            <Fields cols={2}>
              <Field label="Banka">
                <TextInput
                  value={form.banka}
                  maxLength={50}
                  onChange={(e) => setForm({ ...form, banka: e.target.value })}
                />
              </Field>
              <Field label="Účet">
                <TextInput
                  value={form.ucet}
                  maxLength={25}
                  onChange={(e) => setForm({ ...form, ucet: e.target.value })}
                />
              </Field>
            </Fields>
          </div>

          <div className="mt-6">
            <SectionHead n="04" title="Modulové oprávnění" />
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {MODUL_FIELDS.map(({ key, label }) => (
                <Checkbox
                  key={key}
                  label={label}
                  checked={form.moduly.includes(key)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      moduly: e.target.checked
                        ? [...form.moduly, key]
                        : form.moduly.filter((m) => m !== key),
                    })
                  }
                />
              ))}
            </div>
          </div>

          <div className="mt-6">
            <SectionHead n="05" title="LMS pobočky" />
            <p className="mb-3 font-mono text-[10.5px] text-ink-3">
              Každá pobočka má vlastní LMS účet (DID + klíče). Leady a skladové vozy se stahují ze
              všech poboček; katalog vozů se čte přes hlavní pobočku.
            </p>
            {form.lms_pobocky.length === 0 ? (
              <p className="mb-3 border border-line px-4 py-3 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                Bez integrace na LMS.
              </p>
            ) : (
              <div className="mb-3 flex flex-col gap-3">
                {form.lms_pobocky.map((row, i) => (
                  <div
                    key={row.key}
                    data-testid="company-lms-pobocka"
                    className="border border-line bg-paper px-4 py-3"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                        Pobočka {i + 1}
                      </span>
                      {i === 0 && <Marker tone="accent">Hlavní</Marker>}
                      {!(Number.parseInt(row.lms_id, 10) > 0 && row.lms_req_key.trim()) && (
                        <Marker tone="warn">Neúplná · leady se nestahují</Marker>
                      )}
                      <span className="ml-auto flex items-center gap-2">
                        {i > 0 && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => makeMainLmsRow(row.key)}
                          >
                            Nastavit jako hlavní
                          </Button>
                        )}
                        <IconButton
                          size="sm"
                          aria-label={`Odebrat pobočku ${i + 1}`}
                          onClick={() => void removeLmsRow(row)}
                        >
                          <Icon name="close" size={12} />
                        </IconButton>
                      </span>
                    </div>
                    <Fields cols={2}>
                      <Field
                        label="Název pobočky"
                        hint={form.lms_pobocky.length > 1 ? 'Zobrazuje se u leadů.' : 'Povinný při více pobočkách.'}
                      >
                        <TextInput
                          value={row.nazev}
                          maxLength={100}
                          placeholder="např. Ostrava"
                          onChange={(e) => setLmsRow(row.key, { nazev: e.target.value })}
                        />
                      </Field>
                      <Field label="LMS ID (DID)">
                        <TextInput
                          type="number"
                          value={row.lms_id}
                          onChange={(e) => setLmsRow(row.key, { lms_id: e.target.value })}
                        />
                      </Field>
                      <Field
                        label="Request key"
                        hint="HMAC klíč pro podpis odchozích volání. Skryto v UI."
                      >
                        <TextInput
                          type="password"
                          value={row.lms_req_key}
                          autoComplete="off"
                          onChange={(e) => setLmsRow(row.key, { lms_req_key: e.target.value })}
                        />
                      </Field>
                      <Field
                        label="Response key"
                        hint="HMAC klíč pro ověření příchozích volání. Skryto v UI."
                      >
                        <TextInput
                          type="password"
                          value={row.lms_res_key}
                          autoComplete="off"
                          onChange={(e) => setLmsRow(row.key, { lms_res_key: e.target.value })}
                        />
                      </Field>
                    </Fields>
                  </div>
                ))}
              </div>
            )}
            <Button size="sm" icon={<Icon name="plus" size={12} />} onClick={addLmsRow}>
              Přidat pobočku
            </Button>
          </div>

          <div className="mt-6">
            <SectionHead n="06" title="Místa předání" />
            <p className="mb-3 font-mono text-[10.5px] text-ink-3">
              Nabídka nebo smlouva nabízí tato místa v poli „Místo předání“ a tiskne jejich adresu. Místo, které
              už některá nabídka či smlouva používá, nelze odebrat.
            </p>
            {form.mista_predani.length === 0 ? (
              <p className="mb-3 border border-line px-4 py-3 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                Zatím žádné místo předání.
              </p>
            ) : (
              <div className="mb-3 flex flex-col gap-3">
                {form.mista_predani.map((row, i) => (
                  <div
                    key={row.key}
                    data-testid="company-misto-predani"
                    className="border border-line bg-paper px-4 py-3"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                        Místo {i + 1}
                      </span>
                      <span className="ml-auto">
                        <IconButton
                          size="sm"
                          aria-label={`Odebrat místo předání ${i + 1}`}
                          onClick={() => removeMistoRow(row.key)}
                        >
                          <Icon name="close" size={12} />
                        </IconButton>
                      </span>
                    </div>
                    <Fields cols={2}>
                      <Field label="Název" required span>
                        <TextInput
                          value={row.nazev}
                          maxLength={50}
                          placeholder="např. Autosalon Trutnov"
                          onChange={(e) => setMistoRow(row.key, { nazev: e.target.value })}
                        />
                      </Field>
                      <Field label="Ulice">
                        <TextInput
                          value={row.ulice}
                          maxLength={50}
                          onChange={(e) => setMistoRow(row.key, { ulice: e.target.value })}
                        />
                      </Field>
                      <Field label="Město">
                        <TextInput
                          value={row.mesto}
                          maxLength={50}
                          onChange={(e) => setMistoRow(row.key, { mesto: e.target.value })}
                        />
                      </Field>
                      <Field label="PSČ">
                        <TextInput
                          value={row.psc}
                          maxLength={11}
                          onChange={(e) => setMistoRow(row.key, { psc: e.target.value })}
                        />
                      </Field>
                    </Fields>
                  </div>
                ))}
              </div>
            )}
            <Button size="sm" icon={<Icon name="plus" size={12} />} onClick={addMistoRow}>
              Přidat místo předání
            </Button>
          </div>

          {mode === 'edit' && company && (
            <div className="mt-6">
              <SectionHead n="07" title="Slack notifikace" />
              <SlackBlock companyId={company.id_firmy} />
            </div>
          )}

          {mode === 'edit' && company && <MembersBlock companyId={company.id_firmy} />}
          {/* Rendered inside the company dialog so it stacks as a nested layer —
              as a sibling, clicking it counts as "outside" and closes the form. */}
          {confirmDialog}
        </Modal.Body>
        <Modal.Foot
          meta={
            mode === 'edit' ? (
              <span>
                <b>Uložením</b> přepíšete data firmy. Smazání firmy s navázanými daty vyžaduje
                potvrzení názvem firmy.
              </span>
            ) : (
              'Esc · zavřít · Enter · uložit'
            )
          }
          actions={
            <>
              {mode === 'edit' && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={onDelete}
                  disabled={remove.isPending}
                >
                  {confirmingDelete ? 'Opravdu smazat?' : 'Smazat'}
                </Button>
              )}
              <Button variant="secondary" size="md" onClick={() => onOpenChange(false)}>
                Zrušit
              </Button>
              <Button variant="primary" size="md" onClick={submit} disabled={isPending}>
                {isPending ? 'Ukládám…' : mode === 'edit' ? 'Uložit' : 'Vytvořit'}
              </Button>
            </>
          }
        />
      </Modal>
      <CompanyDeleteModal
        open={blockedPayload != null}
        payload={blockedPayload}
        company={company}
        onOpenChange={(o) => {
          if (!o) setBlockedPayload(null);
        }}
        onDeleted={() => onOpenChange(false)}
      />
    </>
  );
}

/**
 * Self-contained Slack config block (edit mode only). Talks to the dedicated
 * /v1/companies/:id/slack endpoints — independent of the main company form,
 * since the bot token is a secret that's never read back (only its
 * configured/enabled status is shown).
 */
function SlackBlock({ companyId }: { companyId: number }) {
  const statusQuery = useCompanySlackQuery(companyId);
  const updateSlack = useUpdateCompanySlackMutation(companyId);
  const testSlack = useTestCompanySlackMutation(companyId);
  const [token, setToken] = useState('');
  const status = statusQuery.data;

  async function handleSave(nextEnabled?: number) {
    try {
      const body: { slack_bot_token?: string; slack_enabled?: number } = {};
      if (token.trim()) body.slack_bot_token = token.trim();
      if (nextEnabled !== undefined) body.slack_enabled = nextEnabled;
      if (Object.keys(body).length === 0) return;
      await updateSlack.mutateAsync(body);
      setToken('');
      toast.ok('Slack nastavení uloženo');
    } catch (err) {
      toast.error(apiMessage(err, 'Uložení Slacku se nezdařilo'));
    }
  }

  async function handleTest() {
    try {
      await testSlack.mutateAsync();
      toast.ok('Testovací zpráva odeslána na Slack');
    } catch (err) {
      toast.error(apiMessage(err, 'Test se nezdařil'));
    }
  }

  if (statusQuery.isLoading) return <Skeleton className="h-24 w-full" />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 font-mono text-[11px] text-ink-3">
        <span>
          Stav:{' '}
          <b className={status?.slack_configured ? 'text-ok' : 'text-warn'}>
            {status?.slack_configured ? 'token nastaven' : 'token chybí'}
          </b>
        </span>
        <Checkbox
          label="Notifikace zapnuté"
          checked={Boolean(status?.slack_enabled)}
          disabled={updateSlack.isPending}
          onChange={(e) => void handleSave(e.target.checked ? 1 : 0)}
        />
      </div>
      <Fields cols={1}>
        <Field
          label="Slack bot token (xoxb-…)"
          hint="Vloží/přepíše token. Skryto v UI, zpět se nikdy nevrací. Prázdné = beze změny."
        >
          <TextInput
            type="password"
            value={token}
            autoComplete="off"
            placeholder={status?.slack_configured ? '•••••••• (nastaveno)' : 'xoxb-…'}
            onChange={(e) => setToken(e.target.value)}
          />
        </Field>
      </Fields>
      <div className="flex gap-2.5">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          mono
          disabled={updateSlack.isPending || !token.trim()}
          onClick={() => void handleSave()}
        >
          Uložit token
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          mono
          disabled={testSlack.isPending || !status?.slack_configured}
          onClick={() => void handleTest()}
        >
          {testSlack.isPending ? 'Posílám…' : 'Test'}
        </Button>
      </div>
    </div>
  );
}

function emptyForm(): CompanyFormState {
  return {
    Nazev: '',
    ulice: '',
    cp: '',
    mesto: '',
    psc: '',
    ICO: '0',
    DIC: '',
    zapis_OR: '',
    banka: '',
    ucet: '',
    id_skupiny: '',
    moduly: [],
    lms_pobocky: [],
    mista_predani: [],
  };
}

function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

interface SectionHeadProps {
  n: string;
  title: string;
}

function SectionHead({ n, title }: SectionHeadProps) {
  return (
    <header className="mb-3 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
      <span aria-hidden className="inline-block h-px w-3.5 bg-accent" />
      <span className="text-accent-text">{n}</span>
      <span>{title}</span>
    </header>
  );
}

interface MembersBlockProps {
  companyId: number;
}

function MembersBlock({ companyId }: MembersBlockProps) {
  const members = useCompanyMembersQuery(companyId);
  const add = useAddCompanyMemberMutation();
  const updateMember = useUpdateCompanyMemberMutation();
  const removeMember = useRemoveCompanyMemberMutation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const rows = members.data?.memberships ?? [];
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.primarni - a.primarni || a.id_uzivatele - b.id_uzivatele),
    [rows],
  );
  const excludeIds = sorted.map((m) => m.id_uzivatele);

  const setPrimarni = (m: MgmtMembership, value: 0 | 1) => {
    updateMember.mutate(
      { companyId, userId: m.id_uzivatele, body: { primarni: value } },
      {
        onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se uložit.')),
      },
    );
  };

  return (
    <section className="mt-6 border-t border-line pt-5">
      <header className="mb-3 flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3 max-sm:flex-wrap">
        <span aria-hidden className="inline-block h-px w-3.5 bg-accent" />
        <span>Členové</span>
        <span className="ml-auto text-[10px] text-ink-3">
          {members.isLoading ? 'Načítání…' : `${sorted.length} uživatelů`}
        </span>
        <Button
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={() => setPickerOpen(true)}
        >
          Přidat uživatele
        </Button>
      </header>
      <p className="mb-3 font-mono text-[10.5px] text-ink-3">
        Práva modulů spravuje záložka Členové.
      </p>

      {members.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : sorted.length === 0 ? (
        <p className="px-1 py-3 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Žádní členové. Přidejte prvního tlačítkem výše.
        </p>
      ) : (
        <ul className="divide-y divide-line border border-line bg-paper">
          {sorted.map((m) => (
            <li
              key={m.id}
              className="flex items-center gap-3 px-4 py-2.5 text-[12.5px] max-sm:flex-wrap"
            >
              <span className="font-mono text-[11px] tabular-nums text-ink-3">
                #{m.id_uzivatele}
              </span>
              <span className="font-mono text-[12px] text-ink">{m.nick ?? '—'}</span>
              <span className="truncate text-ink-2">
                {[m.jmeno, m.prijmeni].filter(Boolean).join(' ') || '—'}
              </span>
              {m.aktivni === 0 && <Marker tone="danger">Neaktivní</Marker>}
              <span className="ml-auto flex items-center gap-4">
                <Checkbox
                  label="Primární"
                  checked={m.primarni === 1}
                  onChange={(e) => setPrimarni(m, e.target.checked ? 1 : 0)}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    removeMember.mutate(
                      { companyId, userId: m.id_uzivatele },
                      {
                        onSuccess: () => toast.ok('Člen odebrán'),
                        onError: (err) =>
                          toast.error(apiMessage(err, 'Nepodařilo se odebrat.')),
                      },
                    );
                  }}
                >
                  Odebrat
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <MembershipPickerModal
        open={pickerOpen}
        kind="user"
        excludeIds={excludeIds}
        onOpenChange={setPickerOpen}
        onSelect={(u) => {
          add.mutate(
            { companyId, body: { id_uzivatele: u.id_uzivatele } },
            {
              onSuccess: () => toast.ok(`${u.nick} přidán`),
              onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se přidat.')),
            },
          );
        }}
      />
    </section>
  );
}
